import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { classService } from '../../../services/classService';
import AddClassModal from '../../organisms/AddClassModal';

const Container = styled.div`
  max-width: 1200px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  color: #333;
  margin: 0 0 24px;
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f0f0;
`;

const CardTitle = styled.h2`
  font-size: 20px;
  color: #333;
  margin: 0;
`;

const AddButton = styled.button`
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 2px solid #f0f0f0;
`;

const Tab = styled.button`
  padding: 12px 24px;
  background: none;
  border: none;
  border-bottom: 3px solid ${props => props.$active ? '#007bff' : 'transparent'};
  color: ${props => props.$active ? '#007bff' : '#666'};
  font-size: 15px;
  font-weight: ${props => props.$active ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #007bff;
  }
`;

const ClassCardContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 16px;
`;

const ClassCard = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border-left: 4px solid ${props => {
    switch (props.$status) {
      case 'scheduled': return '#007bff';
      case 'ongoing': return '#ffc107';
      case 'completed': return '#28a745';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  }};
  transition: all 0.2s;

  &:hover {
    background-color: #e9ecef;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ClassTitle = styled.h3`
  font-size: 18px;
  color: #333;
  margin: 0 0 12px;
  font-weight: 600;
`;

const ClassInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: #333;
  min-width: 80px;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${props => {
    switch (props.$status) {
      case 'scheduled': return '#e7f3ff';
      case 'ongoing': return '#fff3cd';
      case 'completed': return '#d4edda';
      case 'cancelled': return '#f8d7da';
      default: return '#f0f0f0';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'scheduled': return '#007bff';
      case 'ongoing': return '#ffc107';
      case 'completed': return '#28a745';
      case 'cancelled': return '#dc3545';
      default: return '#666';
    }
  }};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #dee2e6;
`;

const SmallButton = styled.button`
  flex: 1;
  padding: 8px 12px;
  background-color: ${props => props.$variant === 'danger' ? '#dc3545' : props.$variant === 'success' ? '#28a745' : '#6c757d'};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-size: 16px;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  font-size: 16px;
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const STATUS_LABELS = {
  scheduled: '예정',
  ongoing: '진행중',
  completed: '완료',
  cancelled: '취소'
};

const ClassesPage = () => {
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    filterClasses();
  }, [classes, activeTab]);

  const fetchClasses = async () => {
    try {
      setLoading(true);
      setError('');
      const allClasses = await classService.getAllClasses();
      setClasses(allClasses);
    } catch (err) {
      console.error('Failed to fetch classes:', err);
      setError('수업 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const filterClasses = () => {
    if (activeTab === 'all') {
      setFilteredClasses(classes);
    } else {
      setFilteredClasses(classes.filter(c => c.status === activeTab));
    }
  };

  const handleCopyZoomUrl = async (zoomJoinUrl) => {
    try {
      await navigator.clipboard.writeText(zoomJoinUrl);
      setSuccess('Zoom 링크가 클립보드에 복사되었습니다.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setError('링크 복사에 실패했습니다.');
    }
  };

  const handleCompleteClass = async (classId) => {
    if (!window.confirm('수업을 완료 처리하시겠습니까? Zoom API를 통해 참여자 정보와 녹화본 정보를 수집합니다.')) {
      return;
    }

    try {
      setLoading(true);
      await classService.completeClass(classId);
      setSuccess('수업이 완료 처리되었습니다.');
      await fetchClasses();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to complete class:', err);
      setError(err.message || '수업 완료 처리에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm('수업을 삭제하시겠습니까? Zoom 미팅도 함께 삭제됩니다.')) {
      return;
    }

    try {
      setLoading(true);
      await classService.deleteClass(classId);
      setSuccess('수업이 삭제되었습니다.');
      await fetchClasses();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to delete class:', err);
      setError(err.message || '수업 삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate();
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTabCount = (status) => {
    if (status === 'all') return classes.length;
    return classes.filter(c => c.status === status).length;
  };

  return (
    <Container>
      <PageTitle>수업 관리</PageTitle>

      {success && <SuccessMessage>{success}</SuccessMessage>}
      {error && <ErrorMessage>{error}</ErrorMessage>}

      <Card>
        <CardHeader>
          <CardTitle>Zoom 수업 목록</CardTitle>
          <AddButton onClick={() => setIsAddModalOpen(true)}>
            + 새 수업 생성
          </AddButton>
        </CardHeader>

        <TabContainer>
          <Tab
            $active={activeTab === 'all'}
            onClick={() => setActiveTab('all')}
          >
            전체 ({getTabCount('all')})
          </Tab>
          <Tab
            $active={activeTab === 'scheduled'}
            onClick={() => setActiveTab('scheduled')}
          >
            예정 ({getTabCount('scheduled')})
          </Tab>
          <Tab
            $active={activeTab === 'ongoing'}
            onClick={() => setActiveTab('ongoing')}
          >
            진행중 ({getTabCount('ongoing')})
          </Tab>
          <Tab
            $active={activeTab === 'completed'}
            onClick={() => setActiveTab('completed')}
          >
            완료 ({getTabCount('completed')})
          </Tab>
        </TabContainer>

        {loading ? (
          <LoadingMessage>수업 목록을 불러오는 중...</LoadingMessage>
        ) : filteredClasses.length === 0 ? (
          <EmptyMessage>
            {activeTab === 'all'
              ? '생성된 수업이 없습니다. 새 수업을 생성해보세요.'
              : `${STATUS_LABELS[activeTab]} 상태의 수업이 없습니다.`}
          </EmptyMessage>
        ) : (
          <ClassCardContainer>
            {filteredClasses.map((classItem) => (
              <ClassCard key={classItem.id} $status={classItem.status}>
                <ClassTitle>{classItem.title}</ClassTitle>

                <ClassInfo>
                  <InfoRow>
                    <InfoLabel>상태:</InfoLabel>
                    <Badge $status={classItem.status}>
                      {STATUS_LABELS[classItem.status] || classItem.status}
                    </Badge>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>시작 시간:</InfoLabel>
                    {formatDateTime(classItem.scheduledStartTime)}
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>수업 시간:</InfoLabel>
                    {classItem.duration}분
                  </InfoRow>
                  {classItem.participantCount > 0 && (
                    <InfoRow>
                      <InfoLabel>참여자:</InfoLabel>
                      {classItem.participantCount}명
                    </InfoRow>
                  )}
                </ClassInfo>

                <ButtonGroup>
                  <SmallButton
                    onClick={() => handleCopyZoomUrl(classItem.zoomJoinUrl)}
                  >
                    링크 복사
                  </SmallButton>

                  {classItem.status === 'ongoing' && (
                    <SmallButton
                      $variant="success"
                      onClick={() => handleCompleteClass(classItem.id)}
                      disabled={loading}
                    >
                      완료 처리
                    </SmallButton>
                  )}

                  {classItem.status !== 'completed' && (
                    <SmallButton
                      $variant="danger"
                      onClick={() => handleDeleteClass(classItem.id)}
                      disabled={loading}
                    >
                      삭제
                    </SmallButton>
                  )}
                </ButtonGroup>
              </ClassCard>
            ))}
          </ClassCardContainer>
        )}
      </Card>

      <AddClassModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => {
          fetchClasses();
          setSuccess('수업이 성공적으로 생성되었습니다.');
          setTimeout(() => setSuccess(''), 3000);
        }}
      />
    </Container>
  );
};

export default ClassesPage;
